import { Badge } from '@/components/ui/badge';

import { GtPageSplit, GtSheetSplit } from './types';

type SavedSplit = GtPageSplit | GtSheetSplit;

/** Saved page ranges Claude flagged as unsure, as 0-based "start-end" keys. */
export function unsurePageKeys(saved: GtPageSplit[] | undefined): Set<string> {
    return new Set(
        (saved ?? [])
            .filter((row) => row.unsure)
            .map((row) => `${row.startPage}-${row.endPage}`),
    );
}

/** Saved sheets Claude flagged as unsure, by 0-based sheet index. */
export function unsureSheetIndexes(
    saved: GtSheetSplit[] | undefined,
): Set<number> {
    return new Set(
        (saved ?? []).filter((row) => row.unsure).map((row) => row.sheetIndex),
    );
}

/** The small "Claude unsure" marker shown on an editor row Claude was unsure about. */
export function UnsureBadge() {
    return (
        <Badge variant="alert" data-testid="ClaudeDraft__unsureBadge">
            Claude unsure
        </Badge>
    );
}

export const SOURCE_HINTS = {
    Claude: 'Drafted by Claude. Check the pages and type before you verify.',
    Pipeline: 'Made by the ingestion pipeline automatically.',
    Person: 'Made by a person. Someone set these pages and the type by hand.',
};

function describeRow(row: SavedSplit): string {
    const where =
        'sheetTitle' in row
            ? `Sheet "${row.sheetTitle}"`
            : row.startPage === row.endPage
              ? `Page ${row.startPage + 1}`
              : `Pages ${row.startPage + 1}–${row.endPage + 1}`;
    return row.notes ? `${where}: ${row.notes}` : where;
}

/** A small amber note listing the rows Claude was unsure about, until a person has checked them all. */
export function ClaudeUnsureNote({
    saved,
}: {
    saved: SavedSplit[] | undefined;
}) {
    const rows = saved ?? [];
    if (
        !rows.some((row) => row.draftedBy === 'claude') ||
        rows.every((row) => row.checked)
    )
        return null;
    const unsure = rows.filter((row) => row.unsure);
    if (unsure.length === 0) return null;
    return (
        <p
            className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
            data-testid="ClaudeDraftBanner__unsure"
        >
            Unsure: {unsure.map(describeRow).join('; ')}
        </p>
    );
}
